import { useMemo } from 'react';
import { useAreaStore } from '@/stores/area';
import { useProductAreas } from '@/features/products/hooks';
import { type ProductArea } from '@/lib/schemas/product-area.schema';

export const NO_AREA_LABEL = 'Sin área';

export interface AreaSelection {
  // Catálogo de áreas que el back le devuelve a este usuario.
  areas: readonly ProductArea[];
  activeSlug: string | null;
  // Nombre del área activa. "Sin área" si no hay ninguna elegida o si la
  // guardada ya no está en el catálogo.
  activeName: string;
  setActiveArea: (slug: string | null) => void;
  isLoading: boolean;
  isSuccess: boolean;
  // false = el endpoint falló o el catálogo vino vacío: no se ofrece elegir
  // área. Mientras carga sí se ofrece (deshabilitado), para que el control no
  // aparezca de golpe al llegar la respuesta.
  isAvailable: boolean;
}

// Área activa + catálogo + reglas de cuándo se ofrece elegir. Lo comparten el
// selector de la barra (`AreaSelector`) y el submenú "Área" del menú de
// usuario en el celular, así los dos muestran la misma lista con las mismas
// reglas y escriben en el mismo store.
//
// La recuperación de un área guardada que ya no existe NO vive acá: es un
// efecto de `AreaSelector`, que queda montado en todas las pantallas.
export function useAreaSelection(): AreaSelection {
  const activeSlug = useAreaStore((s) => s.activeAreaSlug);
  const setActiveArea = useAreaStore((s) => s.setActiveArea);
  const query = useProductAreas();

  // Memo para que el fallback `[]` no cambie de identidad en cada render y
  // dispare sin motivo los efectos que dependen de la lista.
  const areas = useMemo(() => query.data ?? [], [query.data]);

  // Endpoint caído (back sin deployar) o catálogo vacío = no se ofrece.
  // Reduce ruido cuando el back todavía no tiene la feature activa.
  const isAvailable = !query.isError && (areas.length > 0 || query.isLoading);

  const activeName =
    areas.find((a) => a.slug === activeSlug)?.name ?? NO_AREA_LABEL;

  return {
    areas,
    activeSlug,
    activeName,
    setActiveArea,
    isLoading: query.isLoading,
    isSuccess: query.isSuccess,
    isAvailable,
  };
}
